/* eslint-disable indent */

import * as Sentry from '@sentry/react';
import request from 'utils/request';
import { has } from 'lodash';
import moment from 'moment';
import { GET_WHITELISTING_URL } from 'containers/PaymentLinks/constants';
import { PL_BULK_UPLOAD } from 'containers/Whitelisting/constants';
import { getItemFromLocalStorage } from 'utils/common';
import { VIEW_REFUND_TIMELINE_URL } from 'containers/RefundsDetail/constants';

import {
  FEATURE_TOGGLE_CONFIG,
  PRODUCT_STATUS_URL,
  ON_DEMAND_FREE_TRIAL_ELIGIBLE,
  GET_UNIFICATION_ONBOARDING_URL,
  APPROVED,
  GET_ECOM360_PRODUCT_STATUS_URL,
} from '../utils/constants';

import {
  GET_MERCHANT_DETAILS_URL,
  GET_FEATURE_TOGGLE_URL,
  FETCH_EASY_SPLIT_CONFIG_URL,
  GET_SSO_CONFIG_URL,
  GET_MERCHANT_PG_CONFIG_URL,
  GET_PAYMENT_METHODS_URL,
  GET_ROUTER_ACTIVATION_STATUS_URL,
  CHECK_ON_DEMAND_ELIGIBILITY_URL,
  CHECK_FLOWWISE_ACTIVATION_URL,
  GET_INSTANT_REFUNDS_FREE_TRIAL_ELIGIBILITY_URL,
  GET_WALLET_BANK_DETAILS_URL,
  GET_SHOPIFY_MERCHANT,
  GOOGLE_SHEET_URL,
  GET_ALL_ROUTES_URL,
  GET_ALL_RULE_ATTRIBUTES_URL,
  GET_PG_CAPTURE_CONFIG_URL,
  GET_PRODUCT_ACCESS_LEVEL,
  GET_PRODUCT_FRUIT_CAMPAIGNS,
  GET_SEND_BIRD_URL,
} from '../constants/url';

export const getMerchantConfig = async token => {
  try {
    const { url, method } = GET_MERCHANT_DETAILS_URL;
    const response = await request(url, {
      method,
      token,
      isModuleFederated: true,
    });
    return response;
  } catch (err) {
    return {};
  }
};

export const getProductStatus = async token => {
  try {
    const url = PRODUCT_STATUS_URL;
    const options = {
      method: 'GET',
      token,
      isModuleFederated: true,
    };
    const response = await request(url, options);
    return response;
  } catch (err) {
    return {};
  }
};

export const getFeatureToggleConfig = async () => {
  try {
    const { url, method } = GET_FEATURE_TOGGLE_URL;
    const options = {
      method,
      body: JSON.stringify({
        toggles: FEATURE_TOGGLE_CONFIG.map(e => ({ featureName: e })),
      }),
      isModuleFederated: true,
      getRequestId: true,
    };

    const response = await request(url, options);
    const xRequestId = response?.xRequestId;

    if (response && response.toggles) {
      const obj = response.toggles.reduce((acc, curr) => {
        const { featureName, value } = curr;
        acc[featureName] = value;
        return acc;
      }, {});

      const featureToggleDataWithVariant = response.toggles.reduce(
        (acc, curr) => {
          const { featureName, variant } = curr;
          // eslint-disable-next-line no-param-reassign
          acc[featureName] = variant;
          return acc;
        },
        {},
      );

      return { obj, response: featureToggleDataWithVariant };
    }

    Sentry.withScope(scope => {
      scope.setTag('label', 'feature-toggle');
      scope.setTag('failure_type', 'missing_toggles_in_response');
      if (xRequestId) scope.setTag('x_request_id', xRequestId);
      scope.setContext('feature_toggle_debug', {
        responseKeys: response ? Object.keys(response) : [],
        rawResponse: response,
      });
      Sentry.captureMessage(
        '[FeatureToggle] 200 OK but response.toggles missing',
        'warning',
      );
    });
    return { obj: {}, response: {} };
  } catch (e) {
    const failureType = e?.name === 'CustomError'
      ? 'unexpected_error'
      : e?.isTimeout
        ? 'timeout'
        : 'network_or_fetch_error';

    Sentry.withScope(scope => {
      scope.setTag('label', 'feature-toggle');
      scope.setTag('failure_type', failureType);
      const xRequestId = e?.errorMeta?.xRequestId ?? e?.xRequestId;
      if (xRequestId) scope.setTag('x_request_id', xRequestId);
      scope.setContext('feature_toggle_debug', {
        errorDetail: e.cause?.message ?? e.message,
        statusCode: e?.errorMeta?.statusCode,
      });
      scope.setFingerprint(['feature-toggle', failureType]);
      Sentry.captureException(e);
    });
    return { obj: {}, response: {} };
  }
};

export const fetchEasySplitConfig = async () => {
  const result = {
    easySplitEnabled: false,
    easySplitStatus: '',
    criticalMerchant: false,
    isVpaEnabled: false,
    vendorDashboardEnabled: false,
    staticSplitEnabled: false,
  };
  try {
    const { url } = FETCH_EASY_SPLIT_CONFIG_URL;
    const response = await request(url, { isModuleFederated: true });

    result.easySplitEnabled =
      response?.status.trim().toUpperCase() === 'ACTIVE';
    result.easySplitStatus = response?.status;
    result.criticalMerchant = response?.criticalMerchant;
    result.isVpaEnabled = response?.vpaEnabled;
    result.vendorDashboardEnabled = response?.vendorDashboardEnabled;
    result.staticSplitEnabled = response?.staticSplitEnabled === true;
    return result;
  } catch (e) {
    return result;
  }
};

export const getSSOConfig = async () => {
  try {
    const { url, method } = GET_SSO_CONFIG_URL;
    const response = await request(url, { method, isModuleFederated: true });
    return response || {};
  } catch (e) {
    return {};
  }
};

export const fetchOffersConfig = async () => {
  let offersEnabled = false;
  try {
    const { url, method } = GET_MERCHANT_PG_CONFIG_URL;
    const options = {
      method,
      queryString: { configKey: 'OFFERS' },
      isModuleFederated: true,
    };
    const response = await request(url, options);

    if (response && response.length && response[0].configValue)
      offersEnabled = response[0].configValue.trim().toUpperCase() === 'ACTIVE';

    return offersEnabled;
  } catch (e) {
    return offersEnabled;
  }
};

export const fetchActivePaymentModes = async () => {
  let isUPIEnabled = false;
  let isVbaEnabled = false;
  try {
    const { url, method } = GET_PAYMENT_METHODS_URL;
    const options = {
      method,
      queryString: { currency: 'INR', amount: 1 },
      isModuleFederated: true,
    };
    const response = await request(url, options);

    if (has(response, 'paymentModes.UPI')) isUPIEnabled = true;
    if (has(response, 'paymentModes.VBA_TRANSFER')) isVbaEnabled = true;

    return { isUPIEnabled, isVbaEnabled };
  } catch (e) {
    return { isUPIEnabled, isVbaEnabled };
  }
};

export const fetchRouterActivationStatus = async () => {
  const result = { showRouterTab: false, routerStatus: '' };
  try {
    const { url, method } = GET_ROUTER_ACTIVATION_STATUS_URL;
    const response = await request(url, { method, isModuleFederated: true });

    if (response) {
      result.showRouterTab = response?.showRouterTab ?? true;
      result.routerStatus = response.status;
    }

    return result;
  } catch (e) {
    return result;
  }
};

export const fetchFlowwiseActivationStatus = async () => {
  try {
    const { url, method } = CHECK_FLOWWISE_ACTIVATION_URL;
    const { flowWiseActive } = await request(url, {
      method,
      isModuleFederated: true,
    });
    return flowWiseActive;
  } catch (e) {
    return false;
  }
};

export const fetchOnDemandEligibility = async () => {
  try {
    const { url, method } = CHECK_ON_DEMAND_ELIGIBILITY_URL;
    const response = await request(url, { method, isModuleFederated: true });
    return (
      response && response.eligibleStatus === ON_DEMAND_FREE_TRIAL_ELIGIBLE
    );
  } catch (e) {
    return false;
  }
};

export const fetchUnificationOnboarding = async () => {
  const result = {
    showInstantSettlementsAlertBar: false,
    unificationDate: '',
    showInstantTab: false,
  };

  try {
    const { url, method } = GET_UNIFICATION_ONBOARDING_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
    };
    const response = await request(url, options);
    const { showBanner, showISTab, enabledOn } = response;
    if (showBanner && enabledOn) {
      result.showInstantSettlementsAlertBar = true;
      result.unificationDate = enabledOn;
    } else {
      if (result.showInstantSettlementsAlertBar)
        result.showInstantSettlementsAlertBar = false;
      if (result.unificationDate) result.unificationDate = '';
    }
    if (showISTab) result.showInstantTab = true;
    else if (result.showInstantTab) result.showInstantTab = false;
    return result;
  } catch (err) {
    return result;
  }
};

export const fetchWhitelisting = async () => {
  let showBatchLinkUpload = false;
  try {
    const response = await request(GET_WHITELISTING_URL);
    const isActiveMerchantBulkUpload = (response || []).reduce((acc, curr) => {
      if (curr.referenceType === PL_BULK_UPLOAD && curr.status === APPROVED) {
        // eslint-disable-next-line no-param-reassign
        acc = true;
      }
      return acc;
    }, false);
    showBatchLinkUpload = isActiveMerchantBulkUpload;
    return showBatchLinkUpload;
  } catch (err) {
    return err;
  }
};

export const getInstantRefundsFreeTrialEligibility = async () => {
  try {
    const { url, method } = GET_INSTANT_REFUNDS_FREE_TRIAL_ELIGIBILITY_URL;
    const response = await request(url, {
      method,
      isModuleFederated: true,
      queryString: { merchantId: getItemFromLocalStorage('merchantId') },
    });
    const { eligibilityData, ...result } = response;
    return { ...result, ...eligibilityData };
  } catch (err) {
    return {};
  }
};

export const getWalletBankList = async () => {
  try {
    const { url, method } = GET_WALLET_BANK_DETAILS_URL;
    const response = await request(url, {
      method,
      queryString: {
        tenant: 'pg',
        beneType: 'merchant',
        pageNo: 0,
        pageSize: 2,
      },
    });
    return response;
  } catch (err) {
    return [];
  }
};

export const getShopifyMerchant = async () => {
  try {
    const { url, method } = GET_SHOPIFY_MERCHANT;
    const response = await request(url, {
      method,
      isModuleFederated: true,
    });
    const { shopifyDetails } = response;
    return {
      isShopifyMerchant: !!shopifyDetails?.shopId,
      shopifyStoreUrl: shopifyDetails?.shopId || null,
    };
  } catch (err) {
    return { isShopifyMerchant: false };
  }
};

export const saveUserDetailInSheet = async formData => {
  try {
    const { url } = GOOGLE_SHEET_URL;
    const newFormData = {
      ...formData,
      date: moment().format('YYYY-MM-DD'),
      merchantId: getItemFromLocalStorage('merchantId'),
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newFormData),
      muteHttpExceptions: true,
      mode: 'no-cors',
    });
    return response;
  } catch (error) {
    console.log('err', error);
    throw new Error(error);
  }
};

export const getEcom360ProductStatus = async reqBody => {
  try {
    const { url, method } = GET_ECOM360_PRODUCT_STATUS_URL;
    const options = {
      method,
      body: JSON.stringify(reqBody),
      isModuleFederated: true,
    };
    const response = await request(url, options);
    return response;
  } catch (err) {
    throw new Error(err);
  }
};

export const getAllRoutes = async () => {
  try {
    const { url, method } = GET_ALL_ROUTES_URL;
    const response = await request(url, { method });
    return response;
  } catch (e) {
    return [];
  }
};

export const getAllRuleAttributes = async () => {
  try {
    const { url, method } = GET_ALL_RULE_ATTRIBUTES_URL;
    const response = await request(url, { method });
    return response;
  } catch (e) {
    return [];
  }
};

export const getRefundsTimeline = async query => {
  try {
    const { url, method } = VIEW_REFUND_TIMELINE_URL;
    const response = await request(url, { method, queryString: query });
    return response;
  } catch (e) {
    return [];
  }
};

export const getPgCaptureConfig = async () => {
  try {
    const { url, method } = GET_PG_CAPTURE_CONFIG_URL;
    const { merchantInitiated } = await request(url, { method });
    return merchantInitiated;
  } catch (e) {
    return false;
  }
};

export const getProductPrivilege = async () => {
  try {
    const { url, method } = GET_PRODUCT_ACCESS_LEVEL;
    const response = await request(url, { method });
    return response?.products;
  } catch (e) {
    return false;
  }
};

export const getProductFruitCampaigns = async () => {
  try {
    const { url, method } = GET_PRODUCT_FRUIT_CAMPAIGNS;
    const response = await request(url, { method });
    return response || {};
  } catch (e) {
    return {};
  }
};

export const getSendBirdToken = async merchantId => {
  const { url, method } = GET_SEND_BIRD_URL;
  const modifiedUrl = url
    .replace('$merchantId$', merchantId)
    .replace('$expiresAt$', Date.now() + 30 * 60 * 1000);

  try {
    const response = await request(
      modifiedUrl,
      { method },
      process.env.YELLOW_AI_CHATBOT_BASE_URL,
    );

    return response || { token: '' };
  } catch (error) {
    throw error;
  }
};
